import {Button, Modal} from '@mantine/core';
import {Divider} from 'src/design/atoms/Divider';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {MIME_TYPES} from '@mantine/dropzone';
import {IconCloudDownload} from '@tabler/icons-react';
import React, {useState} from 'react';
import type {IFormDocumentUploadFile} from 'src/app/forms/FormFileDropzone/FormFileUploadV2';
import {FormFileUploadV2} from 'src/app/forms/FormFileDropzone/FormFileUploadV2';
import {emptySplitApi} from 'src/redux/api';
import {useAppDispatch} from 'src/redux/hooks';
import {ApiEndpoints} from 'src/shared/constants/apiEndpoints';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import ApiCaller, {ApiCallerDownloadFile} from 'src/shared/utils/ApiCaller';
import {isValidArray} from 'src/utilities/objectUtils';
import type {IFormFieldV2} from 'src/types/FormV2';

const fieldData: IFormFieldV2 = {
  name: 'standardImport',
  label: 'Standard File Upload',
  type: 'file',
  description:
    'Use the file upload below to select a .CSV file containing your standard data. The data file must match the import template specification. You may download a copy of the template to match with your data using the "Download Template" button below. Note: This import will not check for similar standards in your environment.',
  fileUploadOptions: {
    allowedFileTypes: [MIME_TYPES.csv],
    isHideUploadSuccessEnabled: true,
  },
};

export interface ImportCustomStandardsProps {
  show: boolean;
  onClose: () => void;
  onImportSuccess: () => void;
}

export const ImportCustomStandards: React.FC<ImportCustomStandardsProps> = ({
  show,
  onClose,
  onImportSuccess,
}) => {
  const {setAlert} = useAlert();
  const dispatch = useAppDispatch();
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [importedFiles, setImportedFiles] = useState<IFormDocumentUploadFile[]>(
    []
  );

  const handleUpload = (
    _name: string,
    _value: string,
    uploadedFiles: IFormDocumentUploadFile[]
  ) => {
    setImportedFiles(uploadedFiles);
  };

  const handleConfirmImport = () => {
    setIsSaving(true);

    if (isValidArray(importedFiles)) {
      importedFiles.forEach((file, index) => {
        ApiCaller({
          method: 'POST',
          endpointURL: `${ApiEndpoints.customStandardImport}/${file.encodedKey}`,
        })
          .then(() => {
            if (index + 1 === importedFiles.length) {
              dispatch(
                emptySplitApi.util.invalidateTags(['ComplianceStandards'])
              );
              onImportSuccess();
            }
          })
          .catch(err => {
            setAlert(
              (err as {response?: {data?: {message?: string}}})?.response?.data
                ?.message || 'error downloading the file',
              'error'
            );
            setIsSaving(false);
          });
      });
    }
  };

  const handleDownloadImportTemplate = () => {
    ApiCallerDownloadFile(
      ApiEndpoints.customStandardImportTemplate,
      'standard_import_template.csv'
    ).catch((error: unknown) => setAlert(parseErrorMessage(error), 'error'));
  };

  const handleClose = () => {
    setImportedFiles([]);
    onClose();
  };

  return (
    <Modal
      opened={show}
      onClose={handleClose}
      title="Import Company Standards"
      size="lg">
      <Stack gap="md">
        <Divider />
        <FormFileUploadV2
          field={fieldData}
          value={importedFiles}
          onChange={handleUpload}
        />
        <Group justify="space-between">
          <Button
            color="dark"
            variant="subtle"
            onClick={handleDownloadImportTemplate}
            leftSection={<IconCloudDownload size={16} />}>
            Download Template
          </Button>
          <Group>
            <Button color="dark" variant="subtle" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmImport}
              disabled={!isValidArray(importedFiles)}
              loading={isSaving}>
              {`Import ${
                importedFiles.length > 1
                  ? `${importedFiles.length} Files`
                  : 'File'
              }`}
            </Button>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ImportCustomStandards;
